// askSmart-ui/src/pages/TOSP/tabs/OverviewTab.tsx
import React, { useMemo, useState } from "react";
import LeaseLapsedModal from "../components/LeaseLapsedModal";
import TenantModal from "../components/TenantModal";
import TenantGrid, {
  BreakpointProgress,
  TENANT_SUMMARY_COLUMNS,
} from "../components/TenantGrid";
import { formatCurrency } from "../format";
import { breakpointProgress, tenantBreakpointDetail } from "../portal/portalSummary";
import {
  psfBand,
  PsfBand,
  psfQuartiles,
  tenantGroc,
  TenantGroc,
} from "../portal/tenantGroc";
import { camMonthsByTenant } from "../portal/tenantExpenses";
import { salesYearToDate, useStoredSales } from "../portal/tenantSales";
import { useTospChrome } from "../TospChromeContext";
import { useTospData } from "../TospDataContext";
import { currentTenants, lapsedTenancies, Tenant, todayISO } from "../types";

// Overview — the tenant list to read, not to change. Same grid as the Tenants
// tab, without the Add, Edit or Delete controls; View opens the full record in
// the tenant form, read-only.
/**
 * When each lapsed tenancy answered "not yet" should be asked about again.
 *
 * A WEEK, and in localStorage rather than per session. "Not yet" means the
 * renewal is outstanding, which lasts days or weeks — so the question is still
 * worth putting again, but not on every browser refresh. Session-scoped was the
 * first attempt and it asked again on each new session, which for an admin who
 * reopens the tab all day is the same as asking constantly.
 *
 * Stored as tenant id → the date to ask again, in plain ISO. A date rather than a
 * timestamp because the cadence is daily-granular: snoozing on Tuesday means
 * Tuesday next week, not 168 hours later to the minute.
 *
 * Deliberately not stored on the tenancy: "somebody clicked Not Yet on Tuesday"
 * is not a fact about the lease, and a flag there would have to be cleared by
 * whatever finally renewed it. The durable record of the question is the red
 * traffic light, which stays until the lease is renewed or the tenancy ended.
 *
 * Per BROWSER, not per admin — two people on two machines are each asked once a
 * week. Acceptable: the light is what guarantees the question isn't lost.
 */
const DEFERRED_KEY = "tosp.lapsedDeferred.v2";
const DEFER_DAYS = 7;

type Deferrals = Record<string, string>;

const readDeferred = (): Deferrals => {
  try {
    const raw = window.localStorage.getItem(DEFERRED_KEY);
    const parsed = raw ? (JSON.parse(raw) as Deferrals) : {};
    // Expired snoozes are dropped on read, so the store doesn't accumulate a row
    // per tenancy for ever and a stale entry can't suppress anything.
    const today = todayISO();
    return Object.fromEntries(
      Object.entries(parsed).filter(([, askOn]) => typeof askOn === "string" && askOn > today),
    );
  } catch {
    // Private mode, or storage blocked. The only consequence is being asked
    // again, which is better than failing to render the screen.
    return {};
  }
};

const writeDeferred = (deferrals: Deferrals): void => {
  try {
    window.localStorage.setItem(DEFERRED_KEY, JSON.stringify(deferrals));
  } catch {
    /* as above */
  }
};

/** A week on from today, as ISO. */
const askAgainDate = (): string => {
  const date = new Date();
  date.setDate(date.getDate() + DEFER_DAYS);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

export default function OverviewTab() {
  const { units, tenants, buildingName, unitById, configuration, taxRolls, budgets, saveOccupancy } =
    useTospData();
  const { sales } = useStoredSales();
  const { focus, clearFocus } = useTospChrome();
  const [viewing, setViewing] = useState<Tenant | null>(null);
  const [deferred, setDeferred] = useState<Deferrals>(readDeferred);
  const [movingOut, setMovingOut] = useState(false);

  /**
   * Leases that have run out with no decision recorded, oldest first.
   *
   * Asked one at a time, in that order — the longest overrun first, so the queue
   * drains from the back of the backlog forward.
   */
  const lapsed = useMemo(() => lapsedTenancies(tenants), [tenants]);

  // Arriving from a traffic light's Go: that tenant is asked about regardless of
  // having been deferred, because clicking Go IS asking to be asked again.
  const forced = focus ? lapsed.find((row) => row.tenant.id === focus) ?? null : null;
  const queue = forced ? [forced] : lapsed.filter((row) => !deferred[row.tenant.id]);
  const asking = queue[0] ?? null;

  const defer = () => {
    if (!asking) return;
    const next = { ...deferred, [asking.tenant.id]: askAgainDate() };
    setDeferred(next);
    writeDeferred(next);
    clearFocus();
  };

  /**
   * They have gone: the exit date is the lease's last day.
   *
   * Writing it is the whole move. Everything the admin was promised follows from
   * this one field — `isPastTenancy` starts returning true, so the tenancy leaves
   * the Tenants tab and this screen and appears under Past Tenants, and
   * `isOccupancyActive` starts returning false, so the unit reads as unoccupied
   * on the Units tab and in the traffic lights.
   */
  const moveOut = async () => {
    if (!asking) return;
    setMovingOut(true);
    try {
      await saveOccupancy(asking.tenant.id, {
        ...asking.occupancy,
        leaseEndDate: asking.endedOn,
      });
      clearFocus();
    } catch {
      /* surfaced by the provider */
    } finally {
      setMovingOut(false);
    }
  };

  const year = new Date().getFullYear();

  // The businesses still trading. A departed tenancy has no breakpoint left to
  // track and no current ratio, and leaving it in would also drag the Sales PSF
  // quartiles below — the bands are meant to say where a tenant sits against
  // the centre as it is now, not against businesses that have gone.
  const current = useMemo(() => currentTenants(tenants), [tenants]);

  // The tenant's reported sales this year against the breakpoint their leases
  // add up to — the same pair the tenant sees on their own dashboard, so the
  // two never disagree about how close they are to percentage rent.
  const progressFor = (tenant: Tenant): BreakpointProgress | null => {
    const breakpoint = tenantBreakpointDetail(tenant, unitById, configuration, year).total;
    // No space on any of their leases attracts percentage rent, so there is no
    // breakpoint to reach — reported as N/A rather than left blank, which would
    // be indistinguishable from a tenant who simply hasn't filed yet.
    if (breakpoint === null) return { state: "none" };

    const salesToDate = salesYearToDate(sales, tenant.id, year)?.total ?? null;
    const percent = breakpointProgress(salesToDate, breakpoint);
    if (percent === null) return null;

    return {
      state: "tracking",
      percent,
      label: `${formatCurrency(salesToDate as number)} of ${formatCurrency(breakpoint)}`,
    };
  };

  /**
   * Sales PSF and GROC for every tenant, worked out ONCE.
   *
   * Each one builds up to two years of that tenant's statement — rent from the
   * lease, taxes from the roll budgets, both BMVA charges — and the grid asks
   * for the same tenant twice, once to sort and once to render. Done per render
   * across fifty tenants that is enough work to block the first few frames,
   * which is what killed the reveal animation: the bars had no chance to paint
   * before the clock driving them had run most of its course, so the column
   * arrived blank and then appeared complete.
   *
   * Recomputed only when something it reads actually changes.
   */
  // The CAM allocation for each budget year, worked out ONCE for the whole
  // portfolio. GROC needs it per tenancy, and computing it per tenancy would
  // be fifty-odd divisions of eleven categories across ninety units — the
  // shape of computation that once blocked this screen's first frames and
  // broke its reveal animation.
  const camsByYear = useMemo(() => {
    const byYear = new Map<number, Map<string, number[]> | null>();
    budgets.forEach((budget) => {
      if (byYear.has(budget.year)) return;
      byYear.set(budget.year, camMonthsByTenant(budget, units, tenants));
    });
    return byYear;
  }, [budgets, units, tenants]);

  const groc = useMemo(() => {
    const camMonths = (year: number, tenantId: string) =>
      camsByYear.get(year)?.get(tenantId);
    const byTenant = new Map<string, TenantGroc | null>();
    for (const tenant of current) {
      byTenant.set(
        tenant.id,
        tenantGroc({ tenant, unitById, configuration, taxRolls, sales, budgets, camMonths }),
      );
    }
    return byTenant;
  }, [current, unitById, configuration, taxRolls, sales, budgets, camsByYear]);

  const grocFor = (tenant: Tenant): TenantGroc | null => groc.get(tenant.id) ?? null;

  /**
   * The quarter boundaries, from EVERY tenant that has a Sales PSF.
   *
   * Across the whole portfolio, not the rows on screen: a colour that changed
   * with the sort order or a filter would mean something different each time it
   * was looked at, and the key beside the column would describe a set the
   * reader can't see. Computed once per render — the cache above means the
   * statements behind it are built once, not twice.
   */
  const quartiles = psfQuartiles(current.map((tenant) => grocFor(tenant)?.salesPsf));

  const psfBandFor = (tenant: Tenant): PsfBand | null =>
    psfBand(grocFor(tenant)?.salesPsf, quartiles);

  return (
    <section className="tosp-card">
      <LeaseLapsedModal
        lapsed={asking}
        remaining={Math.max(0, queue.length - 1)}
        onConfirm={moveOut}
        onCancel={defer}
        busy={movingOut}
      />

      <TenantGrid
        tenants={current}
        columns={TENANT_SUMMARY_COLUMNS}
        emptyMessage="No tenants yet. Add them on the Tenants tab."
        onView={setViewing}
        progressFor={progressFor}
        grocFor={grocFor}
        psfBandFor={psfBandFor}
        // One button on this screen, not three — the width it doesn't need
        // goes to the columns carrying figures.
        actionsWidth="0.6fr"
      />

      <TenantModal
        isOpen={!!viewing}
        tenant={viewing}
        units={units}
        tenants={tenants}
        buildingName={buildingName}
        unitById={unitById}
        onClose={() => setViewing(null)}
        readOnly
      />
    </section>
  );
}
